import { MatsuriError } from "./MatsuriError";

export interface FetcherResult<T, U = Record<string, unknown>> {
  data: T | undefined;
  error: MatsuriError<U> | undefined;
}

export type AuthTokenHeader =
  | "Authorization"
  | "X-Access-Token"
  | "x-authorization"
  // @see https://github.com/matsuri-tech/m2m-systems
  | "X-M2m-Access-Token";

export interface FetcherOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE" | "OPTIONS" | "HEAD";
  token?: string;
  body?: RequestInit["body"];
  fetch?: (info: RequestInfo, init?: RequestInit) => Promise<Response>;
  authTokenHeader?: AuthTokenHeader;
}

export const fetcher = async <T, U = Record<string, unknown>>(
  url: string,
  options: FetcherOptions = {},
): Promise<FetcherResult<T, U>> => {
  try {
    return { data: await fetcherThrows(url, options), error: undefined };
  } catch (e) {
    return { data: undefined, error: e as MatsuriError<U> };
  }
};

export const bearerFetcher = <T, U = Record<string, unknown>>(
  token: string,
  url: string,
  options?: FetcherOptions,
): Promise<FetcherResult<T, U>> => {
  return fetcher(url, { ...options, token, authTokenHeader: "Authorization" });
};
export const fetcherThrows = async <T>(
  url: string,
  options: FetcherOptions = {},
): Promise<T> => {
  const obj: RequestInit = {};
  if (options.token !== null && options.token !== undefined) {
    const header = options.authTokenHeader ?? "X-Access-Token";

    obj.headers = {
      [header]:
        header === "Authorization" ? `Bearer ${options.token}` : options.token,
    };
  }
  if (options.method) {
    obj.method = options.method || "GET";
  }

  const hasBody = !(options?.body === null || options?.body === undefined);

  if (hasBody) {
    obj.body = options.body;
  }

  if (hasBody && obj.method === "GET") {
    throw new TypeError("The `GET` method cannot be used with a body");
  }

  if (typeof obj.body === "string") {
    obj.headers = {
      "Content-Type": "application/json",
      ...(obj.headers ?? {}),
    };
  }

  // eslint-disable-next-line no-restricted-globals -- This is the fetcher implementation itself, so direct use of fetch is necessary
  const resp = await (options.fetch ?? fetch)(url, obj);

  if (!resp.ok) {
    throw new MatsuriError(await resp.text(), { status: resp.status });
  }

  const bodyText = await resp.text();
  // responseが空の場合があるのでケアする
  return bodyText ? (JSON.parse(bodyText) as T) : (undefined as T);
};
